import React, { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Select from "@cloudscape-design/components/select";
import Toggle from "@cloudscape-design/components/toggle";
import ColumnLayout from "@cloudscape-design/components/column-layout";

import { gamesSectionModalStore } from "../stores/gamesSectionModal";
import { gameSectionStore } from "../stores/gamesSection";
import {
	AVAILABLE_SECTION_PAGES,
	PAGE_COLORS,
	PAGE_LABELS,
	normalizePriorityBySelectedPages,
	normalizeSectionSlug,
	validateSectionSlug,
} from "../helpers/gamesSectionAdmin";

function GamesSectionModal({ Interface }) {
	const store = useRecoilValue(gamesSectionModalStore);
	const setStore = useSetRecoilState(gamesSectionModalStore);
	const gameSection = useRecoilValue(gameSectionStore);

	const [fieldErrors, setFieldErrors] = useState({});
	const [isLoading, setIsLoading] = useState(false);

	const isEdit = store.mode === "edit";
	const form = store.formData;

	const update = (key, value) => {
		setStore({ ...store, formData: { ...cloneDeep(store.formData), [key]: value } });
		if (fieldErrors[key]) setFieldErrors({ ...fieldErrors, [key]: null });
	};

	const validate = () => {
		const errors = {};

		const slugResult = validateSectionSlug(form.name);
		if (!slugResult.valid) {
			errors.name = slugResult.error;
		} else if (gameSection.data.some((s) => (isEdit ? s?.id !== form.id : true) && s?.name === slugResult.slug)) {
			errors.name = "This slug already exists.";
		}

		if (!form.status?.value) errors.status = "Status is required.";
		if (!String(form.icon || "").trim()) errors.icon = "Icon is required.";
		if (!Array.isArray(form.page) || form.page.length === 0) errors.page = "Select at least one page.";

		setFieldErrors(errors);
		return { valid: Object.keys(errors).length === 0, slug: slugResult.slug };
	};

	const handleSave = async () => {
		const v = validate();
		if (!v.valid) {
			Interface.addNoty("Error", "Fix the highlighted errors before saving.");
			return;
		}

		const safePriority = normalizePriorityBySelectedPages(
			form.page,
			form.priority_by_page
		);

		const data = {
			name: v.slug,
			status: form.status.value,
			icon: form.icon,
			page: form.page,
			priority_by_page: safePriority,
		};
		if (isEdit) data.id = form.id;

		setIsLoading(true);
		Interface.request("get", { class: "data", module: "gamesSection", method: isEdit ? "edit" : "add" }, data)
			.then(() => {
				setIsLoading(false);
				setStore({ ...store, show: false });
				Interface.loadGamesSection();
				Interface.addNoty("Success", `Section ${isEdit ? "updated" : "created"}`);
			})
			.catch((err) => {
				setIsLoading(false);
				if (typeof err?.text !== "string") Interface.addNoty("Error", `Couldn't ${isEdit ? "update" : "create"} section`);
			});
	};

	const handleIcon = (e) => {
		const file = e.target.files[0];
		if (file && file.type === "image/svg+xml") {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => update("icon", reader.result);
		} else {
			Interface.addNoty("Error", "Please upload a valid SVG file");
		}
	};

	const dismiss = () => {
		setStore({ ...store, show: false });
		setFieldErrors({});
	};

	const togglePage = (page, checked) => {
		const current = Array.isArray(form.page) ? form.page : [];
		update("page", checked ? [...current, page] : current.filter((p) => p !== page));
	};

	return (
		<Modal
			visible={store.show}
			onDismiss={dismiss}
			header={isEdit ? "Edit Section" : "New Section"}
			size="medium"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="link" onClick={dismiss} disabled={isLoading}>Cancel</Button>
						<Button variant="primary" onClick={handleSave} loading={isLoading}>
							{isEdit ? "Save" : "Create"}
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				<ColumnLayout columns={2}>
					<FormField label="Slug" errorText={fieldErrors.name} description="Unique identifier (lowercase, hyphens)">
						<Input
							autoFocus
							autoComplete="off"
							placeholder="e.g. summer-promo"
							value={form.name}
							onChange={(e) => update("name", normalizeSectionSlug(e.detail.value))}
						/>
					</FormField>

					<FormField label="Status" errorText={fieldErrors.status}>
						<Select
							selectedOption={form.status}
							onChange={(e) => update("status", e.detail.selectedOption)}
							options={[
								{ value: "active", label: "Active" },
								{ value: "inactive", label: "Inactive" },
							]}
						/>
					</FormField>
				</ColumnLayout>

				<FormField label="Icon (SVG)" errorText={fieldErrors.icon}>
					<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
						{form.icon && (
							<div style={{
								width: 48, height: 48, border: "1px solid #e9ebed", borderRadius: "8px",
								display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa",
							}}>
								<img src={form.icon} alt="Icon" style={{ maxWidth: 32, maxHeight: 32 }} />
							</div>
						)}
						<div>
							<input type="file" accept="image/svg+xml" onChange={handleIcon} />
						</div>
					</div>
				</FormField>

				<FormField label="Display on pages" errorText={fieldErrors.page} description="Section will appear on selected pages (use Placement tab to control per-audience visibility)">
					<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
						{AVAILABLE_SECTION_PAGES.map((p) => {
							const on = (form.page || []).includes(p);
							return (
								<button
									key={p}
									type="button"
									onClick={() => togglePage(p, !on)}
									style={{
										padding: "5px 14px", borderRadius: "6px", cursor: "pointer",
										border: on ? `2px solid ${PAGE_COLORS[p]}` : "2px solid #d5dbdb",
										background: on ? PAGE_COLORS[p] : "#fff",
										color: on ? "#fff" : "#414d5c",
										fontSize: "13px", fontWeight: on ? 600 : 400,
										transition: "all 0.15s",
									}}
								>
									{PAGE_LABELS[p]}
								</button>
							);
						})}
					</div>
				</FormField>
			</SpaceBetween>
		</Modal>
	);
}

export default GamesSectionModal;
